import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bitcoin, 
  Shield, 
  Zap, 
  DollarSign, 
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Wallet,
  Globe,
  Lock,
  Smartphone,
  Users,
  Code,
  Percent,
  TrendingDown,
  ChevronDown
} from 'lucide-react';
import './LandingPage.css';

// FAQ Accordion Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div 
      className={`faq-item ${isOpen ? 'open' : ''}`} 
      onClick={() => setIsOpen(!isOpen)}
      style={{
        background: isOpen ? 'rgba(74, 222, 128, 0.05)' : 'rgba(255, 255, 255, 0.02)',
        border: `1px solid ${isOpen ? 'rgba(74, 222, 128, 0.3)' : 'rgba(255, 255, 255, 0.06)'}`,
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginBottom: '12px'
      }}
    >
      <div 
        className="faq-question"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px'
        }}
      >
        <h4 style={{ 
          color: isOpen ? '#4ade80' : '#e4e6eb', 
          fontSize: '1.05rem', 
          fontWeight: '500', 
          margin: 0 
        }}>{question}</h4>
        <ChevronDown 
          size={20} 
          style={{ 
            color: isOpen ? '#4ade80' : '#6b7280',
            flexShrink: 0,
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }} 
        />
      </div>
      <div 
        className="faq-answer"
        style={{
          maxHeight: isOpen ? '200px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease'
        }}
      >
        <p style={{ 
          padding: '0 24px 20px 24px', 
          color: '#9ca3af', 
          fontSize: '0.95rem', 
          lineHeight: '1.6', 
          margin: 0 
        }}>{answer}</p>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [cryptoPrices, setCryptoPrices] = useState({});
  const [priceLoading, setPriceLoading] = useState(true);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [inquiryStatus, setInquiryStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCryptoPrices();
    const interval = setInterval(fetchCryptoPrices, 30000); // Update every 30 seconds
    // Fire-and-forget landing visit tracking (for conversion analytics)
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${BACKEND_URL}/api/analytics/track-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: 'main' }),
    }).catch(() => {});
    return () => clearInterval(interval);
  }, []);

  const fetchCryptoPrices = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/crypto-prices`);
      const data = await response.json();
      setCryptoPrices(data);
      setPriceLoading(false);
    } catch (error) {
      console.error('Failed to fetch crypto prices:', error);
      setPriceLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setInquiryStatus({ type: '', message: '' });

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inquiryForm),
      });

      if (response.ok) {
        setInquiryStatus({
          type: 'success',
          message: 'Thank you! We\'ll contact you shortly to discuss custom solutions for your business.'
        });
        setInquiryForm({ name: '', email: '', phone: '', message: '' });
      } else {
        setInquiryStatus({
          type: 'error',
          message: 'Failed to submit inquiry. Please try again.'
        });
      }
    } catch (error) {
      setInquiryStatus({
        type: 'error',
        message: 'An error occurred. Please try again later.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInquiryChange = (e) => {
    setInquiryForm({
      ...inquiryForm,
      [e.target.name]: e.target.value
    });
  };

  const features = [
    {
      icon: <Smartphone size={32} />,
      title: 'Point of Sale (POS)',
      description: 'Complete tablet-based POS system for in-person crypto payments with QR code scanning'
    },
    {
      icon: <Code size={32} />,
      title: 'Website Integration',
      description: 'Embeddable JavaScript widget for seamless crypto checkout on any website'
    },
    {
      icon: <Users size={32} />,
      title: 'Agent & Partner Program',
      description: 'Revenue sharing model for agents and partners with customizable splits'
    },
    {
      icon: <Percent size={32} />,
      title: 'Custom Solutions',
      description: 'Tailored pricing and features designed specifically for your business needs'
    },
    {
      icon: <Shield size={32} />,
      title: 'Multi-Cryptocurrency Support',
      description: '300+ cryptocurrencies including Bitcoin, Ethereum, Litecoin, and stablecoins'
    },
    {
      icon: <Wallet size={32} />,
      title: 'Multiple Payment Methods',
      description: 'Payment links, POS, e-commerce - accept crypto payments everywhere'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <nav className="navbar" data-testid="navbar">
          <div className="nav-container">
            <div className="logo" data-testid="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/auth')}
              data-testid="get-started-btn"
            >
              Get Started <ArrowRight size={18} />
            </button>
          </div>
        </nav>

        <div className="hero-content" data-testid="hero-content">
          <div className="hero-badge">
            <Bitcoin size={16} />
            <span>Powered by NOWPayments & Dwolla</span>
          </div>
          <h1 className="hero-title" data-testid="hero-title">
            Complete Crypto Payment Platform
            <br />
            <span className="gradient-text">For Businesses & Partners</span>
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Accept crypto payments in-store, online, and everywhere.
            <br />
            POS system • Website integration • Agent program • Custom fees
          </p>
          <div
            className="hero-specialty-badge"
            data-testid="hero-specialty-badge"
            onClick={() => navigate('/peptides')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/peptides'); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              padding: '10px 18px',
              background: 'rgba(74, 222, 128, 0.08)',
              border: '1px solid rgba(74, 222, 128, 0.3)',
              borderRadius: '999px',
              color: '#4ade80',
              fontSize: '0.92rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.2s ease, border-color 0.2s ease',
            }}
          >
            <Shield size={16} />
            <span>
              We specialize in peptide processing with sponsor banks such as{' '}
              <strong style={{ color: '#86efac' }}>Santander</strong>
            </span>
            <ArrowRight size={14} />
          </div>
          <div className="hero-stats">
            <div className="stat">
              <Shield size={24} className="stat-icon" />
              <div className="stat-label">Secure</div>
            </div>
            <div className="stat">
              <Zap size={24} className="stat-icon" />
              <div className="stat-label">Fast</div>
            </div>
            <div className="stat">
              <Bitcoin size={24} className="stat-icon" />
              <div className="stat-label">Multiple Cryptos</div>
            </div>
            <div className="stat">
              <Globe size={24} className="stat-icon" />
              <div className="stat-label">Global Reach</div>
            </div>
            <div className="stat">
              <Lock size={24} className="stat-icon" />
              <div className="stat-label">Self-Hosted</div>
            </div>
            <div className="stat">
              <CheckCircle size={24} className="stat-icon" />
              <div className="stat-label">24/7 Support</div>
            </div>
          </div>
          <div className="hero-cta">
            <button className="btn btn-primary btn-large" onClick={() => navigate('/auth')}>
              Start Accepting Crypto <ArrowRight size={20} />
            </button>
            <button className="btn btn-secondary btn-large" onClick={() => navigate('/auth')}>
              Become an Agent
            </button>
          </div>
        </div>

        <div className="crypto-ticker" data-testid="crypto-ticker">
          <div className="ticker-container">
            {!priceLoading && cryptoPrices.bitcoin && (
              <>
                <div className="ticker-item">
                  <Bitcoin size={16} />
                  <span className="crypto-name">BTC</span>
                  <span className="crypto-price">${cryptoPrices.bitcoin.usd.toLocaleString()}</span>
                  <span className={`crypto-change ${cryptoPrices.bitcoin.usd_24h_change >= 0 ? 'positive' : 'negative'}`}>
                    {cryptoPrices.bitcoin.usd_24h_change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {Math.abs(cryptoPrices.bitcoin.usd_24h_change).toFixed(2)}%
                  </span>
                </div>
                {cryptoPrices.ethereum && (
                  <div className="ticker-item">
                    <div className="crypto-icon">◈</div>
                    <span className="crypto-name">ETH</span>
                    <span className="crypto-price">${cryptoPrices.ethereum.usd.toLocaleString()}</span>
                    <span className={`crypto-change ${cryptoPrices.ethereum.usd_24h_change >= 0 ? 'positive' : 'negative'}`}>
                      {cryptoPrices.ethereum.usd_24h_change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {Math.abs(cryptoPrices.ethereum.usd_24h_change).toFixed(2)}%
                    </span>
                  </div>
                )}
                {cryptoPrices.litecoin && (
                  <div className="ticker-item">
                    <span className="crypto-name">LTC</span>
                    <span className="crypto-price">${cryptoPrices.litecoin.usd.toLocaleString()}</span>
                    <span className={`crypto-change ${cryptoPrices.litecoin.usd_24h_change >= 0 ? 'positive' : 'negative'}`}>
                      {cryptoPrices.litecoin.usd_24h_change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {Math.abs(cryptoPrices.litecoin.usd_24h_change).toFixed(2)}%
                    </span>
                  </div>
                )}
                {cryptoPrices['bitcoin-cash'] && (
                  <div className="ticker-item">
                    <span className="crypto-name">BCH</span>
                    <span className="crypto-price">${cryptoPrices['bitcoin-cash'].usd.toLocaleString()}</span>
                    <span className={`crypto-change ${cryptoPrices['bitcoin-cash'].usd_24h_change >= 0 ? 'positive' : 'negative'}`}>
                      {cryptoPrices['bitcoin-cash'].usd_24h_change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {Math.abs(cryptoPrices['bitcoin-cash'].usd_24h_change).toFixed(2)}%
                    </span>
                  </div>
                )}
                {cryptoPrices.dogecoin && (
                  <div className="ticker-item">
                    <span className="crypto-name">DOGE</span>
                    <span className="crypto-price">${cryptoPrices.dogecoin.usd.toLocaleString()}</span>
                    <span className={`crypto-change ${cryptoPrices.dogecoin.usd_24h_change >= 0 ? 'positive' : 'negative'}`}>
                      {cryptoPrices.dogecoin.usd_24h_change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {Math.abs(cryptoPrices.dogecoin.usd_24h_change).toFixed(2)}%
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" data-testid="features-section">
        <div className="container">
          <h2 className="section-title" data-testid="features-title">Why Crypto Cashier?</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={feature.title} data-testid={`feature-card-${index}`}>
                <div className="feature-icon" data-testid={`feature-icon-${index}`}>{feature.icon}</div>
                <h3 className="feature-title" data-testid={`feature-title-${index}`}>{feature.title}</h3>
                <p className="feature-description" data-testid={`feature-description-${index}`}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="platform-section">
        <div className="platform-container">
          <h2 className="section-title">Complete Payment Ecosystem</h2>
          <p className="section-subtitle">Everything you need to accept crypto payments</p>

          <div
            className="peptide-specialty-callout"
            data-testid="peptide-specialty-callout"
            onClick={() => navigate('/peptides')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/peptides'); }}
            style={{
              maxWidth: '920px',
              margin: '28px auto 0',
              padding: '22px 28px',
              background:
                'linear-gradient(135deg, rgba(74, 222, 128, 0.08) 0%, rgba(59, 130, 246, 0.06) 100%)',
              border: '1px solid rgba(74, 222, 128, 0.25)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '18px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'transform 0.2s ease, border-color 0.2s ease',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: '52px',
                height: '52px',
                borderRadius: '12px',
                background: 'rgba(74, 222, 128, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4ade80',
              }}
            >
              <Shield size={26} />
            </div>
            <div>
              <h3 style={{ color: '#fff', fontSize: '1.1rem', margin: '0 0 4px 0' }}>
                Specialists in Peptide Card Processing → USDT Payouts
              </h3>
              <p style={{ color: '#9ca3af', margin: 0, fontSize: '0.95rem', lineHeight: 1.6 }}>
                For peptide merchants we work with sponsor banks such as{' '}
                <strong style={{ color: '#e4e6eb' }}>Santander</strong> to clear customer
                credit/debit card payments — and we settle your payouts directly in{' '}
                <strong style={{ color: '#e4e6eb' }}>USDT</strong> to your wallet.{' '}
                <span style={{ color: '#4ade80', fontWeight: 600 }}>
                  Learn more about our peptide program →
                </span>
              </p>
            </div>
          </div>
          
          <div className="platform-grid">
            <div className="platform-card">
              <Smartphone size={40} className="platform-icon" />
              <h3>POS System</h3>
              <p>Tablet-optimized point of sale for in-person transactions with QR code support</p>
              <ul className="feature-list">
                <li><CheckCircle size={16} /> Product catalog</li>
                <li><CheckCircle size={16} /> Shopping cart</li>
                <li><CheckCircle size={16} /> Cashier accounts</li>
                <li><CheckCircle size={16} /> Real-time payments</li>
              </ul>
            </div>

            <div className="platform-card">
              <Globe size={40} className="platform-icon" />
              <h3>E-Commerce Integration</h3>
              <p>Embed crypto checkout on any website with our JavaScript widget</p>
              <ul className="feature-list">
                <li><CheckCircle size={16} /> One-line integration</li>
                <li><CheckCircle size={16} /> Modal & redirect modes</li>
                <li><CheckCircle size={16} /> Customizable styling</li>
                <li><CheckCircle size={16} /> Mobile responsive</li>
              </ul>
            </div>

            <div className="platform-card">
              <Users size={40} className="platform-icon" />
              <h3>Agent Program</h3>
              <p>Partner with us and earn revenue share on every transaction</p>
              <ul className="feature-list">
                <li><CheckCircle size={16} /> Custom revenue split</li>
                <li><CheckCircle size={16} /> Register businesses</li>
                <li><CheckCircle size={16} /> Real-time dashboard</li>
                <li><CheckCircle size={16} /> Monthly reports</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Section */}
      <section className="inquiry-section">
        <div className="inquiry-container">
          <h2 className="section-title">Get Custom Solutions for Your Business</h2>
          <p className="section-subtitle">Let us create a tailored plan that fits your needs. Fill out the form below and our team will contact you shortly.</p>
          
          <div className="inquiry-content">
            <div className="inquiry-info">
              <h3>What We Offer</h3>
              <ul className="inquiry-benefits">
                <li><CheckCircle size={20} /> Custom pricing based on your volume</li>
                <li><CheckCircle size={20} /> Flexible payment processing options</li>
                <li><CheckCircle size={20} /> Dedicated account manager</li>
                <li><CheckCircle size={20} /> Priority technical support</li>
                <li><CheckCircle size={20} /> White-label solutions available</li>
                <li><CheckCircle size={20} /> Agent partnership opportunities</li>
              </ul>
            </div>

            <form className="inquiry-form" onSubmit={handleInquirySubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={inquiryForm.name}
                  onChange={handleInquiryChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={inquiryForm.email}
                  onChange={handleInquiryChange}
                  required
                  placeholder="john@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={inquiryForm.phone}
                  onChange={handleInquiryChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Tell Us About Your Business *</label>
                <textarea
                  id="message"
                  name="message"
                  value={inquiryForm.message}
                  onChange={handleInquiryChange}
                  required
                  rows="4"
                  placeholder="Describe your business, expected transaction volume, and any specific requirements..."
                />
              </div>

              {inquiryStatus.message && (
                <div className={`inquiry-status ${inquiryStatus.type}`}>
                  {inquiryStatus.message}
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-large" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Request Custom Quote'}
                <ArrowRight size={20} />
              </button>

              <p className="inquiry-note">
                We typically respond within 24 hours. Your information is kept confidential.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" data-testid="pricing-section">
        <div className="pricing-container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">No hidden fees. No surprises. Just clear pricing.</p>
          
          <div className="pricing-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center', marginTop: '48px' }}>
            <div className="pricing-card" style={{ flex: '0 1 340px', maxWidth: '380px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '20px', padding: '32px' }}>
              <div className="pricing-header">
                <h3 style={{ color: '#fff', fontSize: '1.5rem', marginBottom: '8px' }}>Starter</h3>
                <p className="pricing-desc" style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '24px' }}>Perfect for small businesses</p>
              </div>
              <div className="pricing-price" style={{ marginBottom: '8px' }}>
                <span className="price" style={{ fontSize: '3rem', fontWeight: '700', color: '#fff' }}>$15</span>
                <span className="period" style={{ color: '#6b7280', fontSize: '1rem' }}>/month</span>
              </div>
              <div className="pricing-fee" style={{ marginBottom: '24px', padding: '12px', background: 'rgba(74, 222, 128, 0.1)', borderRadius: '8px', textAlign: 'center' }}>
                <span className="fee-amount" style={{ display: 'block', fontSize: '1.5rem', fontWeight: '700', color: '#4ade80' }}>3%</span>
                <span className="fee-label" style={{ color: '#9ca3af', fontSize: '0.85rem' }}>per transaction</span>
              </div>
              <ul className="pricing-features" style={{ listStyle: 'none', padding: 0, margin: '24px 0' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Unlimited transactions</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> All cryptocurrencies</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Next-day bank settlements</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> POS system access</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Email support</li>
              </ul>
              <button className="btn btn-primary" onClick={() => navigate('/auth')} style={{ width: '100%', marginTop: '16px' }}>
                Get Started
              </button>
            </div>

            <div className="pricing-card featured" style={{ flex: '0 1 340px', maxWidth: '380px', background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.1) 0%, rgba(74, 222, 128, 0.02) 100%)', border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: '20px', padding: '32px', position: 'relative', transform: 'scale(1.05)' }}>
              <div className="featured-badge" style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)', color: '#0a0f1a', padding: '6px 20px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600' }}>Most Popular</div>
              <div className="pricing-header">
                <h3 style={{ color: '#fff', fontSize: '1.5rem', marginBottom: '8px' }}>Business</h3>
                <p className="pricing-desc" style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '24px' }}>For growing businesses</p>
              </div>
              <div className="pricing-price" style={{ marginBottom: '8px' }}>
                <span className="price" style={{ fontSize: '3rem', fontWeight: '700', color: '#fff' }}>$20</span>
                <span className="period" style={{ color: '#6b7280', fontSize: '1rem' }}>/month</span>
              </div>
              <div className="pricing-fee" style={{ marginBottom: '24px', padding: '12px', background: 'rgba(74, 222, 128, 0.1)', borderRadius: '8px', textAlign: 'center' }}>
                <span className="fee-amount" style={{ display: 'block', fontSize: '1.5rem', fontWeight: '700', color: '#4ade80' }}>2.5%</span>
                <span className="fee-label" style={{ color: '#9ca3af', fontSize: '0.85rem' }}>per transaction</span>
              </div>
              <ul className="pricing-features" style={{ listStyle: 'none', padding: 0, margin: '24px 0' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Everything in Starter</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Same-day settlements (+$0.50)</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> E-commerce widget</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Custom branding</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Priority support</li>
              </ul>
              <button className="btn btn-primary" onClick={() => navigate('/auth')} style={{ width: '100%', marginTop: '16px' }}>
                Get Started
              </button>
            </div>

            <div className="pricing-card" style={{ flex: '0 1 340px', maxWidth: '380px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '20px', padding: '32px' }}>
              <div className="pricing-header">
                <h3 style={{ color: '#fff', fontSize: '1.5rem', marginBottom: '8px' }}>Enterprise</h3>
                <p className="pricing-desc" style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '24px' }}>High-volume merchants</p>
              </div>
              <div className="pricing-price" style={{ marginBottom: '8px' }}>
                <span className="price" style={{ fontSize: '3rem', fontWeight: '700', color: '#fff' }}>Custom</span>
              </div>
              <div className="pricing-fee" style={{ marginBottom: '24px', padding: '12px', background: 'rgba(74, 222, 128, 0.1)', borderRadius: '8px', textAlign: 'center' }}>
                <span className="fee-amount" style={{ display: 'block', fontSize: '1.5rem', fontWeight: '700', color: '#4ade80' }}>1%</span>
                <span className="fee-label" style={{ color: '#9ca3af', fontSize: '0.85rem' }}>or lower</span>
              </div>
              <ul className="pricing-features" style={{ listStyle: 'none', padding: 0, margin: '24px 0' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Everything in Business</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> Dedicated account manager</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> API access</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> White-label options</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e4e6eb', fontSize: '0.9rem', padding: '8px 0' }}><CheckCircle size={18} style={{ color: '#4ade80' }} /> 24/7 phone support</li>
              </ul>
              <button className="btn btn-secondary" onClick={() => document.querySelector('.inquiry-section').scrollIntoView({ behavior: 'smooth' })} style={{ width: '100%', marginTop: '16px' }}>
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" data-testid="faq-section">
        <div className="faq-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about accepting crypto payments</p>
          
          <div className="faq-accordion">
            <FAQItem 
              question="How do I receive my funds?"
              answer="We convert cryptocurrency payments to USD and deposit directly to your bank account. Next-day settlements are standard, with same-day available for a small fee."
            />
            <FAQItem 
              question="Which cryptocurrencies do you support?"
              answer="We support Bitcoin (BTC), Ethereum (ETH), Litecoin (LTC), USDT, USDC, and 50+ other cryptocurrencies through our NOWPayments integration."
            />
            <FAQItem 
              question="Is there a transaction limit?"
              answer="No limits on the number of transactions. High-volume merchants may qualify for reduced fees."
            />
            <FAQItem 
              question="Do I need technical knowledge?"
              answer="Not at all! Our dashboard is user-friendly. Create a payment link in seconds and share it with your customer via QR code, text, or email."
            />
            <FAQItem 
              question="What about chargebacks?"
              answer="Cryptocurrency payments are final and cannot be reversed. This means no chargebacks, protecting your business from payment fraud."
            />
            <FAQItem 
              question="How do I get started?"
              answer="Sign up for free, complete KYC verification, add your bank account, and start accepting crypto payments. Setup takes about 10 minutes."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Transform Your Business?</h2>
        <p>Join the crypto payment revolution with Crypto Cashier</p>
        <div className="cta-buttons">
          <button className="btn btn-primary btn-large" onClick={() => navigate('/auth')}>
            Start Accepting Crypto <ArrowRight size={20} />
          </button>
          <button className="btn btn-secondary btn-large" onClick={() => navigate('/auth')}>
            Become a Partner
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-logo" data-testid="footer-logo">
            <Bitcoin size={24} />
            <span>Crypto Cashier</span>
          </div>
          <p className="footer-text" data-testid="footer-text">
            © 2025 Crypto Cashier. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;